import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Button } from "@hydra/atom/components";

import { useSelector } from "react-redux";
import { BoxedContent, Header, NoDataFound } from "@/components/common";
import { FinanceMasterTable } from "@/components/finance";
import {
  getBudgetCategoryTableColumns,
  getBudgetCategoryTableData,
} from "@/components/finance/budget/budgetTableData";
import { getBudgetCategories, deleteBudgetCategory } from "@/api/finance/budgetApi";
import { skeletonLoadingRows } from "@/utils/helpers";
import showToast from "@/utils/toast/helpers";
import { selectPermission } from "@/store/userSlice";
import staticObjectMap from "@/utils/maps/dynamicObjectMap";

function BudgetCategory() {
  const navigate = useNavigate();
  const hasPermission = useSelector(selectPermission({ parent: "Model", scope: staticObjectMap.get("BudgetCategoryObjectName"), action: "Insert" }));

  const { data, isLoading } = useQuery(["budget-category"], getBudgetCategories);

  const queryClient = useQueryClient();
  const deleteMutation = useMutation(
    deleteBudgetCategory,
    {
      onError: () => {
        showToast("Could not delete. Try again!", "error");
      },
      onSuccess: () => {
        showToast("Deleted successfully", "success");
        queryClient.invalidateQueries({
          queryKey: ["ledger-category"]
        });
      },
    }
  );

  const renderTable = () => {
    if (!isLoading && !data?.data) {
      return (
        <NoDataFound
          title="No budget category has been added"
          buttonText="Add budget category"
          onClick={() => navigate("/finance/budget-category/new")}
        />
      );
    }

    return (
      <FinanceMasterTable
        objectName="BudgetCategory"
        isLoading={isLoading}
        tableColumns={getBudgetCategoryTableColumns(data?.data || [])}
        data={isLoading ? skeletonLoadingRows(5) : getBudgetCategoryTableData(data?.data)}
        handleDelete={(id) => deleteMutation.mutate(id)}
      />
    );
  };

  return (
    <BoxedContent>
      <Header
        showBreadcrumb
        leftContent={<h1>Budget Category</h1>}
        rightContent={
          hasPermission ? (
            <Button small onClick={() => navigate("/finance/budget-category/new")}>
              Add budget category
            </Button>
          ) : ""
}
      />

      {renderTable()}
    </BoxedContent>
  );
}

export default BudgetCategory;
